import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfigConstants } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faCheck, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Recipient } from '../../../types/recipient';

// The selector is attribute-based and not tag-based, to be able to add this as a row component for a table.
// For more information check https://stackoverflow.com/questions/55446740/how-to-add-row-component-in-table-in-angular-7

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'tr[pace-forms-recipient-list-entry]',
    templateUrl: './recipient-list-entry.component.html',
    styleUrls: ['./recipient-list-entry.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecipientListEntryComponent implements OnInit, OnDestroy, OnChanges {
    @Input()
    public parentForm: FormArray;

    @Input()
    public recipient: Recipient;

    @Output() public recipientChange = new EventEmitter<Recipient>();

    @Output() public delete = new EventEmitter<void>();

    public destroyTrigger$ = new Subject<void>();

    public checkboxControl: FormControl;
    public emailControl: FormControl;
    public formGroup: FormGroup;

    public faCheck = faCheck;
    public faTrashAlt = faTrashAlt;
    public emailPlaceHolder: string;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        formBuilder: FormBuilder,
        private readonly translate: TranslateService,
    ) {
        this.checkboxControl = formBuilder.control({ value: false, disabled: false });
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.emailControl = formBuilder.control({ value: '', disabled: false }, Validators.required);
        this.formGroup = formBuilder.group({
            checkbox: this.checkboxControl,
            email: this.emailControl,
        });
        this.emailPlaceHolder = <string>this.translate.instant('RECIPIENT_LIST_ENTRY_EMAIL_PLACEHOLDER');
    }

    public ngOnInit(): void {
        this.checkboxControl.patchValue(this.recipient.selected);
        this.checkboxControl[this.recipient.defaultEmail ? 'disable' : 'enable']();
        this.emailControl.patchValue(this.recipient.email);
        this.emailControl[this.recipient.editable ? 'enable' : 'disable']();

        Promise.resolve()
            .then(() => {
                this.parentForm.push(this.formGroup);
            })
            .catch((error) => {
                this.log.error(
                    `RecipientListEntryComponent: ngOnInit: push to parent form failed: ${JSON.stringify(error)} `,
                );
            });

        this.checkboxControl.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.destroyTrigger$))
            .subscribe((value: boolean | null) => {
                this.recipient = { ...this.recipient, selected: value ?? false };
                this.recipientChange.emit(this.recipient);
            });
        this.emailControl.valueChanges
            .pipe(debounceTime(ConfigConstants.debounceTimeMs), distinctUntilChanged(), takeUntil(this.destroyTrigger$))
            .subscribe((value: string | null) => {
                this.recipient = { ...this.recipient, email: value ?? '' };
                this.recipientChange.emit(this.recipient);
            });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.recipient) {
            this.recipient = changes.recipient.currentValue as Recipient;
            this.checkboxControl.patchValue(this.recipient.selected);
        }
    }

    public ngOnDestroy(): void {
        this.destroyTrigger$.next();
        this.parentForm.removeAt(this.parentForm.controls.findIndex((c) => c === this.formGroup));
    }
}
